import React, {
  useEffect,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { strCol, dateTimeCol, numCol } from '@integec/grid-tools/lib/cols'
import {
  CriteriaRow,
  Title,
  Content,
  FullHeightSizer,
  Filler,
} from 'core/screen-util'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
  isValidYearRange,
} from 'core/utils'
import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import {
  getApiTargetInfo,
  ExportComponent,
  getEnvConfig,
  getPermitActions,
  getValidYearRange,
} from 'core'
import { connect } from 'react-redux'
import { getPiData, getPiTagsActive, importPiData } from 'api/pgads.js'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'
import PiImportStatusMannual from './PiImportStatusMannual'
import { message, Button, Select, Input, DatePicker } from 'antd'
import { SpinContext, DatePickerContext, ErrorContext } from 'context'
import * as ScreenPermissions from 'core/EventActions'
const headers = [
  dateTimeCol({
    ident: 'gmtDate',
    display: 'GMT Date',
    width: 160,
    isFiltered: true,
  }),
  dateTimeCol({
    ident: 'eptDate',
    display: 'EPT Date',
    width: 160,
    isFiltered: true,
  }),
  strCol({
    ident: 'piTag',
    display: 'PI Tag',
    isFiltered: true,
    width: 260,
  }),
  strCol({ ident: 'valueType', display: 'Value Type', width: 150 }),
  numCol({
    ident: 'value',
    display: 'Value',
    displayFormat: '0.000000',
    isFiltered: true,
    width: 150,
    alignment: 'right',
  }),
  strCol({ ident: 'status', display: 'Status' }),
  modifierCol,
  modifiedDateCol,
]

const PiImport = props => {
  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    DatePickerContext
  )
  const { setError } = useContext(ErrorContext)
  const { setSpinning } = useContext(SpinContext)

  const [data, setData] = useState([])
  const [piTag, setPitag] = useState([])
  const [selectedPiTags, setSelectedPiTag] = useState(['All'])
  const [importStatus, setImportStatus] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState('')

  const hasPiImportDataAccess = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: props.envConfig.authGodMode,
        actionPermits: props.actionPermits,
        eventActions: [ScreenPermissions.PI_DATA_IMPORT],
        matchAny: true,
      }),
    [props.actionPermits, props.envConfig.authGodMode]
  )

  const loadPiTags = useCallback(
    (startDate, endDate) => {
      const date = startDate >= endDate ? startDate : endDate
      setSpinning(true)
      getPiTagsActive(props.apiTargetInfo)(date).fork(setError, data => {
        setSpinning(false)
        data = [{ piTag: 'All' }].concat(data)

        const selectedTags = data
          .filter(p => selectedPiTags.includes(p.piTag))
          .map(p => p.piTag)
        const piOptions = data.map(tag => ({
          key: tag.piTag,
          value: tag.piTag,
          text: tag.piTag,
        }))

        setPitag(piOptions)
        setSelectedPiTag(selectedTags)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.apiTargetInfo]
  )

  // const dateRangeChange = date => {
  //   setSelectedDateRagePicker(date)
  //   loadPiTags(date[0], date[1])
  // }

  const onRetrieve = () => {
    const isValid = isValidYearRange(
      props.validYearRange,
      selectedDateRangePicker[0],
      selectedDateRangePicker[1]
    )
    if (!isValid) return
    if (selectedPiTags.length === 0) {
      message.warning('Please select PI Tag(s)')
      return
    }
    setSpinning(true)

    getPiData(props.apiTargetInfo)({
      startDate: selectedDateRangePicker[0],
      endDate: selectedDateRangePicker[1],
      tagnames: selectedPiTags,
    }).fork(setError, data => {
      setSpinning(false)
      setData(data)
    })
  }

  // const importTask = (apiTargetInfo, startDate, endDate, tagnames) => () =>
  //   Task.of('anything').chain(() => {
  //     if (startDate.isAfter(endDate))
  //       return rejected('Please select valid End Date')
  //     if (tagnames.length <= 0) return rejected('Please select PI Tag(s)')
  //     else
  //       return importPiData(apiTargetInfo)({
  //         startDate: selectedDateRangePicker[0],
  //         endDate: selectedDateRangePicker[1],
  //         tagnames,
  //       })
  //   })

  useEffect(
    _ => loadPiTags(selectedDateRangePicker[0], selectedDateRangePicker[1]),
    [loadPiTags, selectedDateRangePicker]
  )

  const onImport = _ => {
    if (selectedPiTags.length === 0) {
      message.warning('Please select PI Tag(s)')
      return
    }

    const isValid = isValidYearRange(
      props.validYearRange,
      selectedDateRangePicker[0],
      selectedDateRangePicker[1]
    )
    if (!isValid) return

    setSpinning(true)
    importPiData(props.apiTargetInfo)({
      startDate: selectedDateRangePicker[0],
      endDate: selectedDateRangePicker[1],
      tagnames: selectedPiTags,
    }).fork(setError, res => {
      setImportStatus(res)
      onRetrieve()
    })
  }

  const GridRef = useRef()

  return (
    <div
      style={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <CriteriaRow
        style={{
          display: 'flex',
          //   justifyContent: 'space-between',
          width: '100%',
          paddingBottom: '2px',
        }}
      >
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
            minWidth: '200px',
          }}
        >
          PI Data & Import
        </Title>
        {/* <label style={{ marginRight: '10px' }}>Date:</label> */}
        <DatePicker.RangePicker
          allowClear={false}
          // mode="decade"
          value={selectedDateRangePicker}
          placeholder={['Start Date', 'End Date']}
          // open={openPicker}
          onChange={setSelectedDateRagePicker}
          onPanelChange={setSelectedDateRagePicker}
          format="MM/DD/YYYY"
          style={{ minWidth: '200px' }}
        />
        <Button
          style={{ marginLeft: '20px' }}
          primary
          onClick={onRetrieve}
          data-testid="criteria-retrieve"
          type="primary"
        >
          Retrieve
        </Button>
        {hasPiImportDataAccess && (
          <Button
            style={{ marginLeft: '20px' }}
            icon="download"
            onClick={onImport}
          >
            Import Data
          </Button>
        )}
        <Filler />
        <Input.Search
          placeholder="Filter"
          style={{ marginLeft: '20px', maxWidth: '150px' }}
          data-testid="criteria-filter-input"
          value={fuzzyFilter}
          onChange={e => setFuzzyFilter(e.target.value)}
          icon="filter"
        />
        <ExportComponent
          getData={_ => exportDataComponent(GridRef, data, fuzzyFilter)}
          btnStyle={{ marginLeft: '20px' }}
          headers={headers}
          data={data}
          fileName="Export"
          data-testid="criteria-export"
        />
      </CriteriaRow>
      <CriteriaRow style={{ paddingBottom: '2px', paddingTop: '2px' }}>
        <label style={{ minWidth: '100px' }}>Select PI Tag:</label>
        <Select
          mode="multiple"
          style={{ margin: '2px', padding: '2px', width: '100%' }}
          value={selectedPiTags}
          placeholder="Select PI Tag"
          onChange={setSelectedPiTag}
          selection
        >
          {(piTag || []).map(p => (
            <Select.Option key={p.key} value={p.value}>
              {p.text}
            </Select.Option>
          ))}
        </Select>
      </CriteriaRow>
      {importStatus.length > 0 && (
        <PiImportStatusMannual
          data={importStatus}
          hide={_ => setImportStatus([])}
        />
      )}
      <FullHeightSizer heightOffset={240}>
        {({ height, width }) => (
          <Content style={{ height: height - 20 }}>
            <Grid
              ref={GridRef}
              sortEnabled
              data={data}
              headers={headers}
              altBy={u => u.piTag}
              altBgColor="#ebebeb"
              // onEditInfoChange={info => editRow(info)}
              // editInfo={editInfo}
              render={virtualizedGridRenderer({
                autoFixColByKey: true,
                rowHeaderRender,
                headerRowHeight: 30,
                height: height - 60,
                width: width - 50,
              })}
              fuzzyFilter={fuzzyFilter}
              isEditable={false}
              selectionMode="single"
              selectionType="row"
            />
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  validYearRange: getValidYearRange(state),
})

export default connect(mapStateToProps)(PiImport)
